import { useState } from "react";
import { observer } from "mobx-react-lite";
import { MapBase } from "@/components/MapBase";
import { Marker } from "react-map-gl/mapbox";
import { MapPin, Layers, Droplets, FolderTree, Pencil, Info, Maximize2 } from "lucide-react";
import { NetworkTypeBadge } from "@/components/badges/NetworkTypeBadge";
import { Button } from "@/components/ui/button";
import { useStore } from "@/hooks/useStore";
import { MetadataItem } from "./MetadataItem";
import { Box, BoxHeader, BoxContent, Divider } from "./Box";
import { MapPreview } from "./MapPreview";

type StructureDetailsProps = {
	structure: Dryp.Structure;
	onRefresh?: () => void;
};

export const StructureDetails = observer(({ structure, onRefresh }: StructureDetailsProps) => {
	const [isMapPreviewOpen, setIsMapPreviewOpen] = useState(false);
	const {
		uiStore: { modalStore },
		userStore,
	} = useStore();

	const onClickEditNetworkType = () => {
		modalStore.open("editNetworkType", {
			structureId: structure.id,
			onSuccess: () => onRefresh?.(),
			structureNetworkType: structure.networkType,
		});
	};

	return (
		<Box>
			<BoxHeader
				icon={<Info className="w-4 h-4" />}
				title="Structure Details"
				action={
					userStore.canEdit ? (
						<Button variant="outline" size="sm" className="h-7 gap-1.5" onClick={onClickEditNetworkType}>
							<Pencil className="w-3 h-3" />
							Edit
						</Button>
					) : undefined
				}
			/>

			<BoxContent>
				<div
					className="relative w-full h-48 rounded-lg overflow-hidden border border-border group cursor-pointer"
					onClick={() => setIsMapPreviewOpen(true)}
				>
					<MapBase
						initialViewState={{
							latitude: structure.coordinate.latitude,
							longitude: structure.coordinate.longitude,
							zoom: 15,
						}}
						isStatic
					>
						<Marker
							latitude={structure.coordinate.latitude}
							longitude={structure.coordinate.longitude}
							anchor="bottom"
						>
							<MapPin className="w-6 h-6 text-white fill-primary" />
						</Marker>
					</MapBase>
					<div className="absolute top-2 right-2 z-10 h-7 w-7 flex items-center justify-center rounded-md bg-secondary/80">
						<Maximize2 className="w-3.5 h-3.5" />
					</div>
				</div>
				{isMapPreviewOpen && (
					<MapPreview
						latitude={structure.coordinate.latitude}
						longitude={structure.coordinate.longitude}
						onClose={() => setIsMapPreviewOpen(false)}
					/>
				)}
			</BoxContent>

			<BoxContent className="space-y-4">
				<div className="grid grid-cols-2 gap-3">
					<MetadataItem icon={<Layers className="w-4 h-4" />} label="Type">
						<span className="truncate">{structure.type.name}</span>
					</MetadataItem>

					<MetadataItem icon={<Droplets className="w-4 h-4" />} label="System">
						{structure.networkType ? (
							<NetworkTypeBadge type={structure.networkType} showLabel showIndicator={false} />
						) : (
							<span className="text-muted-foreground">Unknown</span>
						)}
					</MetadataItem>
				</div>

				<Divider />

				<div>
					<div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
						<FolderTree className="w-4 h-4" />
						<span>Groups</span>
					</div>
					{structure.groups.length > 0 ? (
						<div className="flex flex-wrap gap-1.5">
							{structure.groups.map(group => (
								<span
									key={group.id}
									className="text-xs px-2 py-0.5 rounded border bg-secondary/50 border-border"
								>
									{group.name}
								</span>
							))}
						</div>
					) : (
						<p className="text-xs text-muted-foreground">Structure does not belong to any group</p>
					)}
				</div>

				{structure.description && (
					<>
						<Divider />
						<div>
							<p className="text-xs text-muted-foreground mb-1">Description</p>
							<p className="text-sm text-muted-foreground">{structure.description}</p>
						</div>
					</>
				)}
			</BoxContent>
		</Box>
	);
});
